import { useRouter } from '@snowball-tech/common/helpers/navigation'
import useTranslations from '@snowball-tech/common/hooks/useTranslations'
import { InputText } from '@snowball-tech/fractal/dist/src/components/InputText'
import { cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilSearchAlt as SearchIcon } from '@tooni/iconscout-unicons-react'
import { usePathname, useSearchParams } from 'next/navigation'

import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useState,
} from 'react'

import debounce from 'lodash/fp/debounce'
import isEmpty from 'lodash/fp/isEmpty'

type Props = {
  onSearch: (query: string, from: 'button' | 'change' | 'keyboard') => void
  className?: string
  fullWidth?: boolean
  searchOnChange?: boolean
  searchOnClear?: boolean
  value?: string
  withUrlParams?: boolean
}
export default function SearchInput({
  className,
  fullWidth = false,
  onSearch,
  searchOnChange = true,
  searchOnClear = true,
  value,
  withUrlParams = true,
}: Props) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const $t = useTranslations()

  const [search, setSearch] = useState(
    (withUrlParams ? searchParams.get('query') : value) || '',
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(debounce(250, onSearch), [onSearch])

  const handleSearchUpdate = (
    _event: ChangeEvent<HTMLInputElement>,
    newValue: string,
  ) => {
    setSearch(newValue)

    if (withUrlParams) {
      const params = new URLSearchParams(searchParams)
      if (isEmpty(newValue)) {
        params.delete('query')
      } else {
        params.set('query', newValue)
      }

      router.replace(`${pathname}?${params.toString()}`)
    }

    if (searchOnChange || (searchOnClear && isEmpty(newValue))) {
      debouncedOnSearch(newValue, 'change')
    }
  }

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch(search, 'keyboard')
    }
  }

  const handleSearch = () => {
    onSearch(search, 'button')
  }

  return (
    <InputText
      aria-label={$t('editions.list.search.input.label')}
      button={{
        icon: <SearchIcon />,
        label: $t('editions.list.search.button.label'),
      }}
      className={cn(
        `[&>div:first-child>div:first-child>div:first-child]:hidden [&>div:first-child>div:first-child>div:first-child]:sm:flex [&_button>div:first-child>div:first-child]:hidden [&_button>div:first-child>div:first-child]:sm:block [&_button>div:first-child>div:last-child]:sm:hidden [&_input]:min-w-[320px] [&_input]:pl-2 [&_input]:sm:pl-6`,
        className,
      )}
      fullWidth={fullWidth}
      placeholder={$t('editions.list.search.input.placeholder')}
      prefix={<SearchIcon />}
      type="search"
      value={search}
      withButton
      onButtonClick={handleSearch}
      onChange={handleSearchUpdate}
      onKeyDown={handleSearchKeyDown}
    />
  )
}
